import { cameraAvailable } from './camera';
import type { PosStore } from './store';
import type { PendingCounts } from './usePos';

/**
 * Geräte-Check „kassenbereit“ (SYN-13): Am Vorabend prüft das Gerät, ob die Kasse am Basar-Tag ohne Netz durchhält.
 * Die Bewertung (`evaluateDeviceCheck`) ist reine Logik; die Signale sammelt `collectSignals` aus Browser und Speicher.
 */

export type CheckStatus = 'ok' | 'warn' | 'fail';

export interface CheckResult {
  id:
    | 'register'
    | 'session'
    | 'catalog'
    | 'catalog-age'
    | 'queue'
    | 'server'
    | 'offline-start'
    | 'install'
    | 'storage-persist'
    | 'storage-space'
    | 'camera';
  label: string;
  status: CheckStatus;
  detail: string;
}

export interface DeviceSignals {
  now: Date;
  registerOpen: boolean;
  hasSession: boolean;
  catalogItems: number;
  /** Serverzeit des letzten vollständigen Abzugs (`snapshotAt`), `null` = nie. */
  snapshotAt: Date | null;
  pendingSales: number;
  failedSales: number;
  /** `true` = der Server hat auf die Prüfanfrage geantwortet, `null` = nicht geprüft. */
  serverReachable: boolean | null;
  serviceWorkerActive: boolean;
  installedAsApp: boolean;
  isIos: boolean;
  /** `null` = der Browser sagt es nicht. */
  storagePersisted: boolean | null;
  storageFreeBytes: number | null;
  camera: 'detector' | 'library' | 'none';
  cameraPermission: 'granted' | 'prompt' | 'denied' | 'unknown';
}

/** Ab so viel freiem Speicher (grob geschätzt) meldet der Check einen Fehler bzw. eine Warnung. */
export const STORAGE_FAIL_BYTES = 20 * 1024 * 1024;
export const STORAGE_WARN_BYTES = 100 * 1024 * 1024;
/** Ein Artikelstand älter als das gilt als veraltet (Preise und neue Artikel könnten fehlen). */
export const CATALOG_STALE_MS = 24 * 60 * 60 * 1000;

const result = (
  id: CheckResult['id'],
  label: string,
  status: CheckStatus,
  detail: string,
): CheckResult => ({ id, label, status, detail });

export function evaluateDeviceCheck(s: DeviceSignals): CheckResult[] {
  const checks: CheckResult[] = [];

  checks.push(
    s.registerOpen
      ? result('register', 'Kasse eröffnet', 'ok', 'Dieses Gerät hat eine Kasse.')
      : result(
          'register',
          'Kasse eröffnet',
          'fail',
          'Bitte zuerst die Kasse eröffnen (einmal mit Netz).',
        ),
  );
  checks.push(
    s.hasSession
      ? result(
          'session',
          'Kassen-Sitzung',
          'ok',
          'Die Übertragung läuft auch, wenn die Anmeldung abläuft.',
        )
      : result(
          'session',
          'Kassen-Sitzung',
          'warn',
          'Keine Kassen-Sitzung gespeichert: Nach einem Ablauf der Anmeldung müssten Sie sich neu anmelden.',
        ),
  );

  checks.push(
    s.catalogItems > 0
      ? result('catalog', 'Artikelliste', 'ok', `${String(s.catalogItems)} Artikel auf dem Gerät.`)
      : result(
          'catalog',
          'Artikelliste',
          'fail',
          'Noch keine Artikel geladen. Bitte mit Netz „Jetzt synchronisieren“.',
        ),
  );
  if (s.catalogItems > 0) {
    if (s.snapshotAt === null) {
      checks.push(
        result('catalog-age', 'Stand der Artikelliste', 'warn', 'Der Stand ist unbekannt.'),
      );
    } else {
      const ageMs = s.now.getTime() - s.snapshotAt.getTime();
      checks.push(
        ageMs > CATALOG_STALE_MS
          ? result(
              'catalog-age',
              'Stand der Artikelliste',
              'warn',
              'Älter als 24 Stunden: Preise oder neue Artikel könnten fehlen. Bitte synchronisieren.',
            )
          : result(
              'catalog-age',
              'Stand der Artikelliste',
              'ok',
              'Aktuell (weniger als 24 Stunden alt).',
            ),
      );
    }
  }

  if (s.failedSales > 0) {
    checks.push(
      result(
        'queue',
        'Warteschlange',
        'fail',
        `${String(s.failedSales)} Verkäufe sind nicht übertragbar. Bitte den Veranstalter fragen.`,
      ),
    );
  } else if (s.pendingSales > 0) {
    checks.push(
      result(
        'queue',
        'Warteschlange',
        'warn',
        `${String(s.pendingSales)} Verkäufe warten auf Übertragung. Bitte mit Netz „Jetzt synchronisieren“.`,
      ),
    );
  } else {
    checks.push(result('queue', 'Warteschlange', 'ok', 'Alle Verkäufe sind übertragen.'));
  }

  if (s.serverReachable === null) {
    checks.push(result('server', 'Verbindung zum Server', 'warn', 'Nicht geprüft.'));
  } else {
    checks.push(
      s.serverReachable
        ? result('server', 'Verbindung zum Server', 'ok', 'Der Server antwortet.')
        : result(
            'server',
            'Verbindung zum Server',
            'warn',
            'Kein Kontakt. Die Kasse arbeitet auch ohne Netz, Abgleich und Aktualisierung aber erst wieder mit Netz.',
          ),
    );
  }

  checks.push(
    s.serviceWorkerActive
      ? result(
          'offline-start',
          'Start ohne Netz',
          'ok',
          'Die App ist für den Start ohne Netz gespeichert.',
        )
      : result(
          'offline-start',
          'Start ohne Netz',
          'warn',
          'Die App ist noch nicht für den Start ohne Netz gespeichert. Bitte einmal mit Netz neu laden.',
        ),
  );

  if (s.isIos && !s.installedAsApp) {
    checks.push(
      result(
        'install',
        'Auf dem Home-Bildschirm',
        'warn',
        'Auf dem iPhone/iPad bitte über „Teilen“ → „Zum Home-Bildschirm“ installieren: Sonst kann iOS lokale Daten verwerfen.',
      ),
    );
  } else {
    checks.push(
      result(
        'install',
        'Auf dem Home-Bildschirm',
        'ok',
        s.installedAsApp
          ? 'Als App installiert.'
          : 'Läuft im Browser (auf diesem Gerät ausreichend).',
      ),
    );
  }

  if (s.storagePersisted === false) {
    checks.push(
      result(
        'storage-persist',
        'Speicher geschützt',
        'warn',
        'Der Browser könnte lokale Daten bei Platzmangel löschen. „Speicher schützen“ anfordern oder die App installieren.',
      ),
    );
  } else {
    checks.push(
      result(
        'storage-persist',
        'Speicher geschützt',
        'ok',
        s.storagePersisted === true
          ? 'Der Speicher ist dauerhaft.'
          : 'Der Browser macht dazu keine Angabe.',
      ),
    );
  }

  if (s.storageFreeBytes !== null) {
    const mb = Math.floor(s.storageFreeBytes / (1024 * 1024));
    checks.push(
      s.storageFreeBytes < STORAGE_FAIL_BYTES
        ? result(
            'storage-space',
            'Freier Speicher',
            'fail',
            `Nur noch etwa ${String(mb)} MB frei. Bitte Platz schaffen.`,
          )
        : s.storageFreeBytes < STORAGE_WARN_BYTES
          ? result('storage-space', 'Freier Speicher', 'warn', `Nur etwa ${String(mb)} MB frei.`)
          : result('storage-space', 'Freier Speicher', 'ok', `Etwa ${String(mb)} MB frei.`),
    );
  }

  if (s.camera === 'none') {
    checks.push(
      result(
        'camera',
        'Kamera-Scan',
        'warn',
        'Keine Kamera-Erkennung verfügbar. Die Eingabe per Hand oder Handscanner funktioniert.',
      ),
    );
  } else if (s.cameraPermission === 'denied') {
    checks.push(
      result(
        'camera',
        'Kamera-Scan',
        'warn',
        'Der Zugriff auf die Kamera ist blockiert. In den Einstellungen des Browsers erlauben.',
      ),
    );
  } else {
    checks.push(
      result(
        'camera',
        'Kamera-Scan',
        'ok',
        s.camera === 'detector'
          ? 'Eingebaute Erkennung des Browsers.'
          : 'Erkennung per Bibliothek (wird bei Bedarf nachgeladen: einmal mit Netz ausprobieren).',
      ),
    );
  }

  return checks;
}

/** Bereit heißt: kein Fehler. Warnungen dürfen bleiben, sind aber zu lesen. */
export function isDeviceReady(checks: readonly CheckResult[]): boolean {
  return checks.every((check) => check.status !== 'fail');
}

interface StorageLike {
  persisted?: () => Promise<boolean>;
  estimate?: () => Promise<{ quota?: number; usage?: number }>;
}

/** Sammelt die Signale aus Browser und lokalem Speicher; jeder Zugriff ist abgesichert (kein Browser kann alles). */
export async function collectSignals(input: {
  store: PosStore;
  pending: PendingCounts;
  now?: Date;
  probeServer?: () => Promise<boolean>;
}): Promise<DeviceSignals> {
  const now = input.now ?? new Date();
  const register = await input.store.register();
  const snapshotAtText = await input.store.getMeta<string>('snapshotAt');
  const snapshotAt = snapshotAtText === undefined ? null : new Date(snapshotAtText);

  let storagePersisted: boolean | null = null;
  let storageFreeBytes: number | null = null;
  const storage = (typeof navigator === 'undefined' ? undefined : navigator.storage) as
    StorageLike | undefined;
  try {
    if (storage?.persisted !== undefined) storagePersisted = await storage.persisted();
    const estimate = await storage?.estimate?.();
    if (estimate?.quota !== undefined && estimate.usage !== undefined) {
      storageFreeBytes = Math.max(0, estimate.quota - estimate.usage);
    }
  } catch {
    // Browser ohne Angabe: der Check zeigt „keine Angabe“
  }

  let cameraPermission: DeviceSignals['cameraPermission'] = 'unknown';
  try {
    const status = await navigator.permissions.query({ name: 'camera' });
    cameraPermission = status.state;
  } catch {
    // Safari kennt die Abfrage nicht
  }
  const detector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

  let serverReachable: boolean | null = null;
  if (input.probeServer !== undefined) {
    try {
      serverReachable = await input.probeServer();
    } catch {
      serverReachable = false;
    }
  }

  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (typeof navigator !== 'undefined' &&
      navigator.platform === 'MacIntel' &&
      navigator.maxTouchPoints > 1);
  const installedAsApp =
    (typeof window !== 'undefined' && window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' &&
      (navigator as { standalone?: boolean }).standalone === true);

  return {
    now,
    registerOpen: register !== undefined,
    hasSession: register?.sessionToken !== undefined && register.sessionToken !== '',
    catalogItems: await input.store.itemCount(),
    snapshotAt: snapshotAt !== null && !Number.isNaN(snapshotAt.getTime()) ? snapshotAt : null,
    pendingSales: input.pending.sales + input.pending.voids,
    failedSales: input.pending.failed,
    serverReachable,
    serviceWorkerActive:
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      navigator.serviceWorker.controller !== null,
    installedAsApp,
    isIos,
    storagePersisted,
    storageFreeBytes,
    camera: detector ? 'detector' : cameraAvailable() ? 'library' : 'none',
    cameraPermission,
  };
}

/** Bittet den Browser, den Speicher der App nicht ungefragt zu löschen (nicht jeder Browser gewährt es). */
export async function requestPersistentStorage(): Promise<boolean> {
  const storage = (typeof navigator === 'undefined' ? undefined : navigator.storage) as
    (StorageLike & { persist?: () => Promise<boolean> }) | undefined;
  try {
    return (await storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}
